import React, { useState, useEffect } from 'react';
import { Calendar, CreditCard, Eye, EyeOff, Save, X, Edit2, Loader2, Trash2, Link2, Tag, Globe, Zap, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../services/api';

const DEVICE_LABELS = {
  android_tv: 'Android TV / Box', firestick: 'Fire TV / Firestick', samsung_lg: 'Smart TV Samsung/LG',
  android_mobile: 'Smartphone/Tablette Android', ios: 'iPhone/iPad', mag_enigma: 'MAG / Enigma2', pc: 'PC / Mac',
};


/**
 * Parse un lien M3U pour en extraire le préfixe, username et password.
 * Format attendu : http://PREFIX.domaine.com/get.php?username=XXX&password=YYY&type=m3u_plus&output=mpegts
 */
const parseM3ULink = (m3u, baseDomain) => {
  const result = { prefix: '', username: '', password: '' };
  if (!m3u) return result;

  try {
    const url = new URL(m3u);
    result.username = url.searchParams.get('username') || '';
    result.password = url.searchParams.get('password') || '';

    const m3uHost = url.hostname; // ex: nbcathrw.xxx-tv4k-xxx.xyz

    if (baseDomain) {
      try {
        const globalHost = new URL(baseDomain).hostname; // ex: xxx-tv4k-xxx.xyz
        if (m3uHost.endsWith('.' + globalHost)) {
          result.prefix = m3uHost.slice(0, m3uHost.length - globalHost.length - 1);
        }
      } catch { /* baseDomain invalide */ }
    }

    if (!result.prefix && m3uHost.split('.').length > 2) {
      result.prefix = m3uHost.split('.')[0];
    }
  } catch {
    const userMatch = m3u.match(/[?&]username=([^&]+)/);
    const passMatch = m3u.match(/[?&]password=([^&]+)/);
    if (userMatch) result.username = userMatch[1];
    if (passMatch) result.password = passMatch[1];
  }

  return result;
};

const getHost = (url) => { try { return new URL(url).hostname; } catch { return ''; } };

// Cherche, parmi les domaines configurés, celui qui correspond à une URL serveur donnée.
const findMatchingDomain = (serverUrl, domains) => {
  if (!serverUrl) return '';
  const host = getHost(serverUrl);
  if (!host) return '';
  for (const d of domains) {
    const dHost = getHost(d);
    if (!dHost) continue;
    if (host === dHost || host.endsWith('.' + dHost)) return d;
  }
  return '';
};

const CUSTOM = '__custom__';

const SubscriptionEditModal = ({ client, subscription, onClose, onSuccess, onDelete }) => {
  const [credentials, setCredentials] = useState({
    username: '', password: '', prefix: '',
    server_url: '', port: '', m3u_link: '',
    access_name: '', smarters_url: ''
  });

  const [endDate, setEndDate] = useState(
    subscription.end_date ? new Date(subscription.end_date).toISOString().split('T')[0] : ''
  );

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [goldenottLoading, setGoldenottLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [goldenottEnabled, setGoldenottEnabled] = useState(false);
  const [goldenottLineId, setGoldenottLineId] = useState(null);
  const [loadingCredentials, setLoadingCredentials] = useState(true);
  const [activeTab, setActiveTab] = useState('credentials');
  const [credentialsId, setCredentialsId] = useState(null);
  const [hasExistingCredentials, setHasExistingCredentials] = useState(false);
  const [promoCode, setPromoCode] = useState(null);

  // DNS multi-domaines
  const [dnsConfig, setDnsConfig] = useState({ domains: [], smarters_domain: '', m3u_output_type: 'mpegts' });
  const [dnsLoaded, setDnsLoaded] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [customServerUrl, setCustomServerUrl] = useState('');
  const [customSmartersUrl, setCustomSmartersUrl] = useState('');
  const [domainInitialized, setDomainInitialized] = useState(false);

  // Charger la config DNS globale (multi-domaines)
  useEffect(() => {
    api.get('/admin/settings/dns/public')
      .then(res => {
        if (res.data) {
          const domains = (res.data.domains && res.data.domains.length > 0)
            ? res.data.domains
            : (res.data.server_url ? [res.data.server_url] : []);
          setDnsConfig({
            domains,
            smarters_domain: res.data.smarters_domain || '',
            m3u_output_type: res.data.m3u_output_type || 'mpegts'
          });
        }
      })
      .catch(() => {})
      .finally(() => setDnsLoaded(true));
  }, []);

  // Charger le code promo utilisé pour cette souscription
  useEffect(() => {
    if (client?.id) {
      api.get(`/admin/user/${client.id}/promo-usage`)
        .then(res => { if (res.data?.promo_code) setPromoCode(res.data); })
        .catch(() => {});
    }
  }, [client?.id]);

  // Charger credentials existants
  useEffect(() => {
    const fetchCredentials = async () => {
      try {
        const response = await api.get(`/admin/subscriptions/${subscription.id}/credentials`);
        if (response.data) {
          setCredentials({
            username: response.data.username || '',
            password: response.data.password || '',
            prefix: response.data.prefix || '',
            server_url: response.data.server_url || '',
            port: response.data.port || '',
            m3u_link: response.data.m3u_link || '',
            access_name: response.data.access_name || '',
            smarters_url: response.data.smarters_url || ''
          });
          setCredentialsId(response.data.id);
          setHasExistingCredentials(true);
          setGoldenottLineId(response.data.goldenott_line_id || null);
          if (response.data.custom_dns_server_url) {
            setCustomServerUrl(response.data.custom_dns_server_url || '');
            setCustomSmartersUrl(response.data.custom_dns_smarters_url || '');
          }
        }
      } catch { /* pas de credentials */ }
      finally { setLoadingCredentials(false); }
    };
    fetchCredentials();
  }, [subscription.id]);

  // Déterminer le domaine sélectionné une fois DNS + credentials chargés
  useEffect(() => {
    if (domainInitialized || !dnsLoaded || loadingCredentials) return;
    const domains = dnsConfig.domains;
    let sel = '';
    if (customServerUrl) {
      sel = CUSTOM;
    } else if (domains.length > 0) {
      sel = findMatchingDomain(credentials.server_url, domains) || domains[0];
    } else if (credentials.server_url) {
      // Aucun domaine configuré mais credential existant → mode personnalisé
      sel = CUSTOM;
      setCustomServerUrl(credentials.server_url);
      setCustomSmartersUrl(credentials.smarters_url || '');
    }
    setSelectedDomain(sel);
    setDomainInitialized(true);
  }, [dnsLoaded, loadingCredentials, domainInitialized, dnsConfig, credentials.server_url, credentials.smarters_url, customServerUrl]);

  // Quand le M3U change → auto-détecter prefix, username, password
  const handleM3UChange = (m3uValue) => {
    const baseDomain = selectedDomain === CUSTOM ? customServerUrl : selectedDomain;
    const parsed = parseM3ULink(m3uValue, baseDomain);
    setCredentials(prev => ({
      ...prev,
      m3u_link: m3uValue,
      prefix: parsed.prefix || prev.prefix,
      username: parsed.username || prev.username,
      password: parsed.password || prev.password,
    }));
  };

  // Ajoute le préfixe au domaine : http://domaine.com → http://prefix.domaine.com
  const addPrefixToUrl = (url, pfx) => {
    if (!url || !pfx) return url;
    try {
      const match = url.match(/^(https?:\/\/)(.+)$/);
      if (match) return `${match[1]}${pfx}.${match[2]}`;
      return url;
    } catch { return url; }
  };

  // Le domaine sélectionné REMPLACE celui du M3U : on régénère
  // server_url, smarters_url et m3u_link à partir du domaine choisi + préfixe + identifiants.
  useEffect(() => {
    if (!domainInitialized) return;
    const baseDomain = selectedDomain === CUSTOM ? customServerUrl : selectedDomain;
    if (!baseDomain) return;

    const prefix = credentials.prefix?.trim() || '';
    const serverUrl = prefix ? addPrefixToUrl(baseDomain, prefix) : baseDomain;

    const smartersBase = selectedDomain === CUSTOM ? customSmartersUrl : dnsConfig.smarters_domain;
    const smartersUrl = prefix && smartersBase ? addPrefixToUrl(smartersBase, prefix) : (smartersBase || '');

    let m3u = credentials.m3u_link;
    if (credentials.username && credentials.password) {
      const out = dnsConfig.m3u_output_type || 'mpegts';
      m3u = `${serverUrl}/get.php?username=${credentials.username}&password=${credentials.password}&type=m3u_plus&output=${out}`;
    }

    setCredentials(prev => {
      if (prev.server_url === serverUrl && prev.smarters_url === smartersUrl && prev.m3u_link === m3u) {
        return prev;
      }
      return { ...prev, server_url: serverUrl, smarters_url: smartersUrl, m3u_link: m3u };
    });
  }, [selectedDomain, customServerUrl, customSmartersUrl, credentials.prefix, credentials.username, credentials.password, dnsConfig, domainInitialized]);

  // Vérifier si l'intégration GoldenOTT est activée (pour afficher le bouton)
  useEffect(() => {
    api.get('/admin/settings/goldenott')
      .then(res => setGoldenottEnabled(!!(res.data?.enabled && res.data?.api_configured)))
      .catch(() => {});
  }, []);

  const refetchCredentials = async () => {
    try {
      const response = await api.get(`/admin/subscriptions/${subscription.id}/credentials`);
      if (response.data) {
        setCredentials({
          username: response.data.username || '',
          password: response.data.password || '',
          prefix: response.data.prefix || '',
          server_url: response.data.server_url || '',
          port: response.data.port || '',
          m3u_link: response.data.m3u_link || '',
          access_name: response.data.access_name || '',
          smarters_url: response.data.smarters_url || ''
        });
        setCredentialsId(response.data.id);
        setHasExistingCredentials(true);
        setGoldenottLineId(response.data.goldenott_line_id || null);
      }
    } catch { /* noop */ }
  };

  const daysRemaining = subscription.days_remaining;
  const renewalEligible = daysRemaining === null || daysRemaining === undefined || daysRemaining <= 30;

  const handleRenewGoldenOTT = async (force = false) => {
    if (!force && !renewalEligible) {
      alert(`Renouvellement anticipé non disponible : il reste ${daysRemaining} jours. Il est possible de renouveler à partir de 30 jours avant expiration.`);
      return;
    }
    if (!window.confirm("Renouveler (prolonger) la ligne GoldenOTT de ce client ? La date d'expiration sera alignée automatiquement.")) return;
    setGoldenottLoading(true);
    try {
      const res = await api.post(`/admin/subscriptions/${subscription.id}/renew-goldenott${force ? '?force=true' : ''}`);
      alert(`Accès renouvelé !${res.data?.new_exp_date ? ' Nouvelle expiration : ' + new Date(res.data.new_exp_date).toLocaleDateString('fr-FR') : ''}`);
      onSuccess();
    } catch (error) {
      const d = error.response?.data?.detail;
      alert(typeof d === 'object' ? (d.message || JSON.stringify(d.errors || d)) : (d || 'Erreur lors du renouvellement GoldenOTT'));
    } finally { setGoldenottLoading(false); }
  };

  const handleGenerateGoldenOTT = async () => {
    if (!window.confirm("Générer automatiquement l'accès IPTV via GoldenOTT pour ce client ?")) return;
    setGoldenottLoading(true);
    try {
      const res = await api.post(`/admin/subscriptions/${subscription.id}/generate-goldenott`);
      if (res.data?.already_generated) {
        alert('Un accès GoldenOTT existe déjà pour cet abonnement.');
      } else {
        alert(`Accès généré avec succès via GoldenOTT${res.data?.is_trial ? ' (essai 24H)' : ''} !`);
      }
      await refetchCredentials();
    } catch (error) {
      const d = error.response?.data?.detail;
      alert(typeof d === 'object' ? (d.message || JSON.stringify(d.errors || d)) : (d || 'Erreur lors de la génération GoldenOTT'));
    } finally { setGoldenottLoading(false); }
  };

  const handleSaveCredentials = async () => {
    setLoading(true);
    try {
      if (hasExistingCredentials && credentialsId) {
        await api.put(`/admin/iptv-credentials/${credentialsId}`, credentials);
      } else {
        await api.post('/admin/iptv-credentials', {
          ...credentials,
          user_id: client.id,
          subscription_id: subscription.id
        });
      }
      alert('Identifiants sauvegardés avec succès');
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally { setLoading(false); }
  };

  const handleSaveEndDate = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/subscriptions/${subscription.id}?end_date=${new Date(endDate).toISOString()}`);
      alert("Date d'expiration modifiée avec succès");
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally { setLoading(false); }
  };

  const getStatusColor = () => {
    switch (subscription.status_icon) {
      case 'green': return 'text-[#00FF88] bg-[#00FF88]/10';
      case 'orange': return 'text-[#FFB800] bg-[#FFB800]/10';
      case 'red': return 'text-[#FF3B5C] bg-[#FF3B5C]/10';
      case 'yellow': return 'text-[#FFB800] bg-[#FFB800]/10';
      default: return 'text-[#6B6B6E] bg-[#3A3A3D]/10';
    }
  };

  const hasAutoFields = !!((selectedDomain && selectedDomain !== CUSTOM) || (selectedDomain === CUSTOM && customServerUrl));

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#1A1A1D] rounded-lg metallic-border max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-white">Gestion Abonnement</h3>
            <p className="text-[#B8B8BA] text-sm">{client.full_name} - {client.email}</p>
            {promoCode && (
              <div className="flex items-center gap-2 mt-1">
                <Tag className="h-3 w-3 text-[#FF6B35]" />
                <span className="text-[#FF6B35] text-xs font-mono">{promoCode.promo_code}</span>
                <span className="text-[#00FF88] text-xs">-{promoCode.discount}€</span>
              </div>
            )}
            <div className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor()}`}>
              {subscription.status === 'active' ? 'Actif' :
               subscription.status === 'pending_credentials' ? 'En attente identifiants' :
               subscription.status === 'expired' ? 'Expiré' : subscription.status}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {onDelete && (
              <button onClick={() => onDelete(subscription)} className="text-[#FF3B5C] hover:text-[#FF2244] p-2 rounded-lg hover:bg-[#FF3B5C]/10 transition-all" title="Supprimer l'abonnement">
                <Trash2 className="h-5 w-5" />
              </button>
            )}
            <button onClick={onClose} className="text-[#6B6B6E] hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex space-x-4 border-b border-[#3A3A3D]">
          <button onClick={() => setActiveTab('credentials')} className={`pb-4 px-6 font-semibold transition-colors ${activeTab === 'credentials' ? 'text-[#00FFFF] border-b-2 border-[#00FFFF]' : 'text-[#B8B8BA] hover:text-white'}`}>
            <div className="flex items-center space-x-2"><CreditCard className="h-4 w-4" /><span>Identifiants IPTV</span></div>
          </button>
          <button onClick={() => setActiveTab('dates')} className={`pb-4 px-6 font-semibold transition-colors ${activeTab === 'dates' ? 'text-[#00FFFF] border-b-2 border-[#00FFFF]' : 'text-[#B8B8BA] hover:text-white'}`}>
            <div className="flex items-center space-x-2"><Calendar className="h-4 w-4" /><span>Dates &amp; Validit&eacute;</span></div>
          </button>
        </div>

        {/* Tab: Identifiants IPTV */}
        {activeTab === 'credentials' && (
          <div className="space-y-4">

            {/* Appareils déclarés par le client (indicatif, par écran) */}
            {Array.isArray(subscription.device_setups) && subscription.device_setups.length > 0 && (
              <div className="bg-[#222226] border border-[#3A3A3D] rounded-lg p-3" data-testid="admin-device-setups">
                <p className="text-[#FFB800] text-xs font-semibold mb-2">Appareils déclarés par le client</p>
                <div className="space-y-1.5">
                  {subscription.device_setups.map((d) => (
                    <div key={d.screen} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                      <span className="text-white font-medium">Écran {d.screen} :</span>
                      <span className="text-[#B8B8BA]">{DEVICE_LABELS[d.device_type] || d.device_type || '—'}</span>
                      {d.app_name && <span className="text-[#FF6B35]">· {d.app_name}</span>}
                      {d.mac_address && <span className="text-[#00FFFF] font-mono">· MAC {d.mac_address}</span>}
                      {d.device_id && <span className="text-[#00FF88] font-mono">· ID {d.device_id}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}


            {/* Génération / renouvellement automatique via GoldenOTT */}
            {goldenottEnabled && !goldenottLineId && (
              <button
                onClick={handleGenerateGoldenOTT}
                disabled={goldenottLoading || loadingCredentials}
                className="w-full bg-gradient-to-r from-[#FFB800] to-[#FF6B35] text-[#0A0A0B] py-3 rounded-lg font-bold flex items-center justify-center space-x-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                data-testid="generate-goldenott-btn"
              >
                {goldenottLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Zap className="h-5 w-5" />}
                <span>Générer l'accès via GoldenOTT</span>
              </button>
            )}
            {goldenottEnabled && goldenottLineId && (
              <div className="bg-[#222226] border border-[#FFB800]/30 rounded-lg p-3 flex flex-col gap-2" data-testid="goldenott-line-banner">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[#FFB800] text-xs sm:text-sm">⚡ Ligne GoldenOTT #{goldenottLineId}</span>
                  <button
                    onClick={() => handleRenewGoldenOTT(false)}
                    disabled={goldenottLoading}
                    className={`px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors disabled:opacity-50 text-sm shrink-0 ${renewalEligible ? 'bg-[#FFB800] text-[#0A0A0B] hover:bg-[#FFC400]' : 'bg-[#3A3A3D] text-[#B8B8BA] cursor-not-allowed'}`}
                    data-testid="renew-goldenott-btn"
                  >
                    {goldenottLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    <span>Renouveler</span>
                  </button>
                </div>
                {!renewalEligible && (
                  <div className="flex items-center justify-between gap-2 text-xs" data-testid="renew-not-eligible-msg">
                    <span className="text-[#6B6B6E]">Anticipé disponible à J-30 (il reste {daysRemaining} j)</span>
                    <button
                      onClick={() => handleRenewGoldenOTT(true)}
                      className="text-[#00FFFF] hover:underline"
                      data-testid="renew-goldenott-force-btn"
                    >
                      Forcer quand même
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Verrouillage des identifiants : lecture seule par défaut */}
            {!editMode ? (
              <button
                onClick={() => setEditMode(true)}
                className="w-full bg-[#222226] border border-[#00FFFF]/40 text-[#00FFFF] py-3 rounded-lg font-semibold flex items-center justify-center space-x-2 hover:bg-[#00FFFF]/10 transition-colors"
                data-testid="edit-credentials-btn"
              >
                <Edit2 className="h-5 w-5" />
                <span>Modifier les identifiants</span>
              </button>
            ) : (
              <div className="bg-[#00FFFF]/10 border border-[#00FFFF]/30 rounded-lg p-3 text-[#00FFFF] text-xs" data-testid="edit-mode-active">
                ✏️ Mode édition activé — les champs sont modifiables. Pensez à enregistrer.
              </div>
            )}

            <fieldset disabled={!editMode} className="space-y-4 border-0 p-0 m-0 disabled:opacity-60">

            {/* 1 — Nom de l'accès */}
            <div>
              <label className="block text-[#E8E8E8] text-sm mb-2">Nom de l'acc&egrave;s <span className="text-[#6B6B6E]">(facultatif)</span></label>
              <input
                type="text"
                value={credentials.access_name}
                onChange={(e) => setCredentials({...credentials, access_name: e.target.value})}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                placeholder="Ex: TV Salon, Box Android..."
                data-testid="access-name-input"
              />
            </div>

            {/* 2 — Choix du domaine DNS */}
            <div className="bg-[#1A1A1D] p-4 rounded-lg border border-[#3A3A3D]">
              <label className="text-[#B8B8BA] text-sm font-semibold flex items-center space-x-2 mb-3">
                <Globe className="h-4 w-4 text-[#00FFFF]" />
                <span>Domaine DNS attribué</span>
              </label>

              {dnsConfig.domains.length === 0 && selectedDomain !== CUSTOM ? (
                <p className="text-[#FFB800] text-xs mb-3">
                  Aucun domaine configuré. Allez dans Paramètres &gt; DNS pour en ajouter, ou choisissez « Personnalisé ».
                </p>
              ) : null}

              <select
                value={selectedDomain}
                onChange={(e) => setSelectedDomain(e.target.value)}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none font-mono"
                data-testid="domain-select"
              >
                {dnsConfig.domains.map((d, i) => (
                  <option key={d} value={d}>{d}{i === 0 ? ' (principal)' : ''}</option>
                ))}
                <option value={CUSTOM}>Personnalisé...</option>
              </select>

              <p className="text-[#6B6B6E] text-xs mt-2">
                Le domaine choisi <strong>remplace</strong> celui du lien M3U lors de la génération des identifiants.
              </p>

              {selectedDomain === CUSTOM && (
                <div className="space-y-3 mt-3">
                  <div>
                    <label className="block text-[#B8B8BA] text-xs mb-1">URL Serveur personnalis&eacute;e</label>
                    <input type="url" value={customServerUrl} onChange={(e) => setCustomServerUrl(e.target.value)}
                      className="w-full bg-[#222226] border border-[#FFB800]/50 rounded-lg px-3 py-2 text-white focus:border-[#FFB800] focus:outline-none font-mono text-sm"
                      placeholder="http://custom.server.com:8080"
                      data-testid="custom-server-url-input" />
                  </div>
                  <div>
                    <label className="block text-[#B8B8BA] text-xs mb-1">URL Smarters personnalis&eacute;e (optionnel)</label>
                    <input type="url" value={customSmartersUrl} onChange={(e) => setCustomSmartersUrl(e.target.value)}
                      className="w-full bg-[#222226] border border-[#FFB800]/50 rounded-lg px-3 py-2 text-white focus:border-[#FFB800] focus:outline-none font-mono text-sm"
                      placeholder="http://custom.smarters.com" />
                  </div>
                </div>
              )}
            </div>

            {/* 3 — Lien M3U (champ principal) */}
            <div className="bg-[#222226] p-4 rounded-lg border border-[#FF6B35]/30">
              <label className="block text-[#FF6B35] text-sm mb-2 font-semibold flex items-center space-x-2">
                <Link2 className="h-4 w-4" />
                <span>Lien M3U</span>
                <span className="text-[#6B6B6E] text-xs font-normal">(les champs ci-dessous seront d&eacute;tect&eacute;s automatiquement)</span>
              </label>
              <input
                type="url"
                value={credentials.m3u_link}
                onChange={(e) => handleM3UChange(e.target.value)}
                className="w-full bg-[#1A1A1D] border border-[#FF6B35]/50 rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none font-mono text-sm"
                placeholder="http://prefix.domaine.com/get.php?username=XXX&password=YYY&type=m3u_plus&output=mpegts"
                data-testid="m3u-link-input"
              />
              {credentials.m3u_link && credentials.username && (
                <p className="text-[#00FF88] text-xs mt-2">Auto-d&eacute;tect&eacute; : pr&eacute;fixe=<strong>{credentials.prefix || '(aucun)'}</strong>, user=<strong>{credentials.username}</strong></p>
              )}
            </div>

            {/* 4 — Préfixe (auto-détecté) */}
            <div>
              <label className="block text-[#E8E8E8] text-sm mb-2">
                Pr&eacute;fixe Client
                {credentials.m3u_link && <span className="ml-2 text-[#00FF88] text-xs">(Auto)</span>}
              </label>
              <input
                type="text"
                value={credentials.prefix}
                onChange={(e) => setCredentials({...credentials, prefix: e.target.value})}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none font-mono"
                placeholder="ex: nbcathrw"
                data-testid="prefix-input"
              />
            </div>

            {/* 5 & 6 — Username + Password côte à côte */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[#E8E8E8] text-sm mb-2">
                  Nom d'utilisateur
                  {credentials.m3u_link && credentials.username && <span className="ml-2 text-[#00FF88] text-xs">(Auto)</span>}
                </label>
                <input
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none font-mono"
                  placeholder="88430F64F177"
                  data-testid="username-input"
                />
              </div>
              <div>
                <label className="block text-[#E8E8E8] text-sm mb-2">
                  Mot de passe
                  {credentials.m3u_link && credentials.password && <span className="ml-2 text-[#00FF88] text-xs">(Auto)</span>}
                </label>
                <div className="relative">
                  <input
                    type={(showPassword || !editMode) ? 'text' : 'password'}
                    value={credentials.password}
                    onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 pr-12 text-white focus:border-[#00FFFF] focus:outline-none font-mono"
                    placeholder="EB3F96B5F6A0"
                    data-testid="password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#00FFFF] hover:text-[#00E5E5]">
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>

            {/* 7 — URL Serveur (auto) */}
            <div>
              <label className="block text-[#E8E8E8] text-sm mb-2">
                URL Serveur
                {hasAutoFields && <span className="ml-2 text-[#00FF88] text-xs">(Auto via domaine + pr&eacute;fixe)</span>}
              </label>
              <input
                type="text"
                value={credentials.server_url}
                onChange={(e) => setCredentials({...credentials, server_url: e.target.value})}
                readOnly={hasAutoFields}
                className={`w-full border rounded-lg px-4 py-3 font-mono text-sm ${
                  hasAutoFields
                    ? 'bg-[#1A1A1D] border-[#00FF88]/30 text-[#00FF88] cursor-not-allowed'
                    : 'bg-[#222226] border-[#3A3A3D] text-white focus:border-[#00FFFF] focus:outline-none'
                }`}
                placeholder="http://server.example.com"
                data-testid="server-url-input"
              />
            </div>

            {/* 8 — URL IPTV Smarters (auto) */}
            <div>
              <label className="block text-[#E8E8E8] text-sm mb-2">
                URL IPTV Smarters Samsung / LG
                {hasAutoFields && <span className="ml-2 text-[#00FF88] text-xs">(Auto)</span>}
              </label>
              <input
                type="url"
                value={credentials.smarters_url}
                onChange={(e) => setCredentials({...credentials, smarters_url: e.target.value})}
                readOnly={hasAutoFields}
                className={`w-full border rounded-lg px-4 py-3 font-mono text-sm ${
                  hasAutoFields
                    ? 'bg-[#1A1A1D] border-[#00FF88]/30 text-[#00FF88] cursor-not-allowed'
                    : 'bg-[#222226] border-[#3A3A3D] text-white focus:border-[#00FFFF] focus:outline-none'
                }`}
                placeholder="http://example.com"
                data-testid="smarters-url-input"
              />
            </div>

            {/* 9 — Port (optionnel) */}
            <div>
              <label className="block text-[#E8E8E8] text-sm mb-2">Port <span className="text-[#6B6B6E]">(optionnel)</span></label>
              <input
                type="text"
                value={credentials.port}
                onChange={(e) => setCredentials({...credentials, port: e.target.value})}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none font-mono"
                placeholder="80"
                data-testid="port-input"
              />
            </div>

            <div className="bg-[#00FFFF]/5 border border-[#00FFFF]/20 rounded-lg p-3">
              <p className="text-[#00FFFF] text-xs">
                ℹ️ Lors de l'enregistrement, le client est automatiquement <strong>notifié par email</strong> si le domaine, l'identifiant ou le mot de passe ont changé.
              </p>
            </div>

            <button
              onClick={handleSaveCredentials}
              disabled={loading || loadingCredentials}
              className="w-full btn-primary py-3 flex items-center justify-center space-x-2 disabled:opacity-50"
              data-testid="save-credentials-btn"
            >
              {loading ? (
                <><Loader2 className="animate-spin h-5 w-5" /><span>Sauvegarde...</span></>
              ) : (
                <><Save className="h-5 w-5" /><span>{hasExistingCredentials ? 'Mettre \u00e0 jour les identifiants' : 'Sauvegarder les identifiants'}</span></>
              )}
            </button>
            </fieldset>
          </div>
        )}

        {/* Tab: Dates */}
        {activeTab === 'dates' && (
          <div className="space-y-4">
            <div className="bg-[#222226] p-4 rounded-lg">
              <h4 className="text-white font-semibold mb-3">Informations actuelles</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#B8B8BA]">Date de d&eacute;but:</span>
                  <span className="text-white">{subscription.start_date ? new Date(subscription.start_date).toLocaleDateString('fr-FR') : 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#B8B8BA]">Date d'expiration actuelle:</span>
                  <span className="text-white font-semibold">{subscription.end_date ? new Date(subscription.end_date).toLocaleDateString('fr-FR') : 'N/A'}</span>
                </div>
                {subscription.days_remaining !== null && (
                  <div className="flex justify-between">
                    <span className="text-[#B8B8BA]">Jours restants:</span>
                    <span className={`font-bold ${subscription.days_remaining > 30 ? 'text-[#00FF88]' : subscription.days_remaining > 0 ? 'text-[#FFB800]' : 'text-[#FF3B5C]'}`}>
                      {subscription.days_remaining > 0 ? subscription.days_remaining : 'Expir\u00e9'}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-[#E8E8E8] text-sm mb-2">Nouvelle date d'expiration</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none" />
            </div>
            <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 p-4 rounded-lg">
              <p className="text-[#FFB800] text-sm">Cette modification sera journalis&eacute;e. L'abonnement sera automatiquement r&eacute;activ&eacute; si une nouvelle date valide est d&eacute;finie.</p>
            </div>
            <button onClick={handleSaveEndDate} disabled={loading || !endDate}
              className="w-full btn-primary py-3 flex items-center justify-center space-x-2 disabled:opacity-50">
              {loading ? (
                <><div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" /><span>Modification...</span></>
              ) : (
                <><Edit2 className="h-5 w-5" /><span>Modifier la date d'expiration</span></>
              )}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default SubscriptionEditModal;
